// RoleAwareRedirect — IA routing.
// CHG-1504 / ITER-3622 (donor-portal γ.2) — original /me bounce.
// CHG-1540 / ITER-3658 — IA correction (4-canonical-paths bounce).
// CHG-1551 / ITER-3668 — IA fix v2 (4-case matrix using activeOrg).
//
// The CHG-1540 "Both-role: platform wins" doctrine was too eager — it
// bounced /overview to /platform even when the user had explicitly
// picked an active org via OrgSwitcher, breaking the user's stated
// intent ("clear active org → back to Platform Dashboard;
// pick org → /overview lands at that org's Home"). Field-test report
// v2 captured this as B2 (/overview unreachable) + N1 (commit body
// vs. spec mismatch).
//
// Post-fix decision matrix (per the CHG-1551 brief):
//
//   is_platform_admin  activeOrg  has_staff_role  has_donor_profile  home
//   -----------------  ---------  --------------  -----------------  --------
//   true               null       (any)           (any)              /platform
//   true               non-null   (any)           (any)              /overview
//   false              (any)      true            (any)              /overview
//   false              (any)      false           true               /me
//   false              (any)      false           false              null (stay)
//
// Bounce surface narrowed to the literal "/" landing path (per
// field-test report B2: /overview is a real destination once the user
// is on it, and intercepting it caused a confusing loop for
// platform-admin-with-active-org accounts). Deep links (e.g.,
// /me/recurring, /donations) are never bounced. Pass-through during
// auth bootstrap + unauthenticated state remains unchanged.

import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";

export interface RoleAwareRedirectProps {
  children: ReactNode;
}

// Resolve the canonical home path for an account's role bundle +
// activeOrg signal. Returns null for the neither-role case (no
// role-appropriate home; stay wherever they landed).
function homePathFor(
  account: {
    is_platform_admin: boolean;
    has_staff_role: boolean;
    has_donor_profile: boolean;
  },
  hasActiveOrg: boolean,
): string | null {
  if (account.is_platform_admin) {
    return hasActiveOrg ? "/overview" : "/platform";
  }
  if (account.has_staff_role) return "/overview";
  if (account.has_donor_profile) return "/me";
  return null;
}

export function RoleAwareRedirect({ children }: RoleAwareRedirectProps) {
  const { account, isAuthenticated, isLoading } = useAuth();
  const { org } = useOrg();
  const location = useLocation();

  if (isLoading || !isAuthenticated || !account) {
    return <>{children}</>;
  }

  // CHG-1551 — bounce ONLY from the literal root path. Pre-fix this
  // also bounced /platform, /overview, /me which created loops for
  // platform-admin-with-active-org (entering /overview directly bounced
  // back to /platform; field-test B2). A user on any non-root path
  // explicitly navigated there — respect it.
  //
  // CHG-1562 / ITER-3678 — IA fix N2. EXCEPTION: /me must also be in
  // the bounce list for accounts WITHOUT a donor profile. Pre-fix a
  // platform admin (no donor profile) navigating to /me fell through
  // to NotFound — /me is the donor-portal root, so RoleAwareRedirect
  // needs to send non-donor accounts to their actual home before the
  // donor router catches the path. Donor-profile accounts at /me pass
  // through unchanged (they belong there).
  if (location.pathname === "/me" && !account.has_donor_profile) {
    const home = homePathFor(account, Boolean(org));
    // home === null only for the neither-role case; bounce to /login
    // so the user gets a coherent landing rather than a 404 / loop.
    return <Navigate to={home ?? "/login"} replace />;
  }

  if (location.pathname !== "/") {
    return <>{children}</>;
  }

  const home = homePathFor(account, Boolean(org));
  if (home === null) {
    // Neither-role account: no role-appropriate home; leave at "/".
    return <>{children}</>;
  }
  if (location.pathname === home) {
    return <>{children}</>;
  }
  return <Navigate to={home} replace />;
}
